import {
  FormControlProps,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  SvgIconProps,
  useTheme,
} from '@mui/material';
import React, { ReactNode, useLayoutEffect, useState } from 'react';
import {
  StyledDropdownFormControl,
  DropdownContainer,
  LeftIconBox,
} from './Dropdown.styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Flex from '@components/Flex/Flex';
import { snakeCase } from 'lodash';

export interface MenuItemObject {
  value: any;
  name: string;
  icon?: ReactNode;
}

interface PropsType extends SelectProps {
  label?: string;
  dropdownId?: string;
  dropdownLabelId?: string;
  leftIcon?: (props: SvgIconProps) => JSX.Element;
  menuItemData?: MenuItemObject[];
  formControl?: FormControlProps;
  helperText?: string;
  menuAutoWidth?: boolean;
}

const Dropdown: React.FC<PropsType> = ({
  label,
  dropdownId = 'Dropdown-Id',
  dropdownLabelId = 'Dropdown-label',
  leftIcon: LeftIcon,
  menuItemData,
  formControl,
  error,
  helperText,
  MenuProps,
  menuAutoWidth = false,
  ...restProps
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useLayoutEffect(() => {
    const onWindowResize = (): void => {
      setAnchorEl(null);
    };
    window.addEventListener('resize', onWindowResize);
    onWindowResize();

    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  useLayoutEffect(() => {
    setAnchorEl(anchorEl);
  }, [anchorEl, anchorEl?.scrollHeight]);

  const getIconColor = () => {
    if (error) return theme.palette.error.main;

    return restProps.value || restProps.defaultValue
      ? theme.palette.common.black
      : theme.palette.divider;
  };

  return (
    <>
      <DropdownContainer onClick={handleClick} error={!!error}>
        {LeftIcon && (
          <LeftIconBox>
            <LeftIcon
              style={{
                color: getIconColor(),
              }}
            />
          </LeftIconBox>
        )}
        <StyledDropdownFormControl
          isLeftIcon={!!LeftIcon}
          variant='filled'
          fullWidth
          {...formControl}
        >
          <InputLabel
            style={{
              color: error ? theme.palette.error.main : theme.palette.divider,
              paddingLeft: !LeftIcon ? theme.spacing(6) : undefined,
            }}
            id={`label_${snakeCase(label)}`}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`label_${snakeCase(label)}`}
            id={dropdownId}
            label={label}
            open={!!open}
            disableUnderline
            MenuProps={{
              anchorEl: anchorEl,
              open: open,
              marginThreshold: 16,
              MenuListProps: {
                sx: {
                  width: !menuAutoWidth
                    ? anchorEl && anchorEl.offsetWidth
                    : 'auto',
                },
              },
              sx: {
                maxHeight: 450,
              },
              ...MenuProps,
            }}
            onDoubleClick={handleClick}
            IconComponent={(props) => (
              <KeyboardArrowDownIcon
                style={{
                  color: getIconColor(),
                }}
                {...props}
              />
            )}
            {...restProps}
          >
            {menuItemData?.map((item, index) => (
              <MenuItem
                key={index}
                value={item.value}
                onClick={(event: any) =>
                  restProps.multiple && event.stopPropagation()
                }
              >
                <Flex
                  overflow='hidden'
                  aria-label={item.name}
                  {...(item.icon && {
                    flexDirection: 'row-reverse',
                    justifyContent: 'flex-end',
                  })}
                >
                  {item.name}
                  {item.icon ? (
                    <div style={{ marginRight: 8 }}>{item.icon}</div>
                  ) : null}
                </Flex>
              </MenuItem>
            ))}
          </Select>
        </StyledDropdownFormControl>
      </DropdownContainer>
      {helperText ? (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      ) : null}
    </>
  );
};

export default Dropdown;
